/*!
 * Cart Drawer — Aldo Panama
 * Side cart pattern modeled after aldoshoes.com.
 *
 * Strategy:
 * - Initial server-render is canonical.
 * - When only qty / line price changes: in-place patch (no DOM rebuild).
 * - When item count crosses 0 ↔ >0 OR items added/removed: re-render body
 *   + footer from the /cart.js JSON using the same templates the server uses.
 * - This avoids the empty-state bug (empty state remaining when items added)
 *   while still feeling fast for qty clicks.
 *
 * Public events:
 *   - cart:open              → open the drawer
 *   - cart:close             → close the drawer
 *   - cart:updated (detail)  → fired after every cart change
 *   - cart:drawer:refresh    → force refetch + full re-render
 */
(function () {
  'use strict';

  /* ── Config ─────────────────────────────────────────────── */

  const CFG = (() => {
    const el = document.getElementById('cart-drawer-config');
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  })();

  /* ── DOM helpers ────────────────────────────────────────── */

  const $ = (s, r) => (r || document).querySelector(s);
  const $$ = (s, r) => Array.from((r || document).querySelectorAll(s));

  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    if (attrs) {
      for (const k in attrs) {
        if (k === 'class') node.className = attrs[k];
        else if (k === 'text') node.textContent = attrs[k];
        else if (k === 'dataset') {
          for (const dk in attrs[k]) node.dataset[dk] = attrs[k][dk];
        } else if (attrs[k] != null && attrs[k] !== false) {
          node.setAttribute(k, attrs[k]);
        }
      }
    }
    children.flat().forEach((c) => {
      if (c == null || c === false) return;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    });
    return node;
  }

  function money(cents, currency) {
    if (typeof cents !== 'number') cents = Number(cents) || 0;
    const ccy = currency || CFG.currency || 'USD';
    try {
      // Símbolo ($) adelante del número, como el filtro `money` de Shopify
      // ("$94.600,00"). No se usa el formato de Intl tal cual: en 'es' pone el
      // código detrás ("110,00 CAD") y moneyWithCurrency() lo repetía
      // ("110,00 CAD CAD"), o el símbolo detrás ("110,00 $").
      const parts = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: ccy,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).formatToParts(cents / 100);
      const symbol = parts.filter((p) => p.type === 'currency').map((p) => p.value).join('');
      const number = parts
        .filter((p) => p.type !== 'currency' && !(p.type === 'literal' && !p.value.trim()))
        .map((p) => p.value)
        .join('');
      return symbol + number;
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function moneyWithCurrency(cents, currency) {
    return money(cents, currency) + ' ' + (currency || CFG.currency || 'USD');
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ── Toast/notification (delegates to cart-notification.liquid) ── */

  const Toast = {
    el: null,
    init() {
      this.el = $('[data-cart-notification]');
      if (this.el && !this.el.querySelector('[data-cart-notification-action]')) {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'cart-notification__action';
        action.dataset.cartNotificationAction = '';
        action.style.display = 'none';
        this.el.appendChild(action);
      }
    },
    show(message, type, opts = {}) {
      if (!this.el) this.init();
      if (!this.el) return;
      const msgEl = this.el.querySelector('.cart-notification__message');
      if (msgEl) msgEl.textContent = message;
      this.el.className = `cart-notification cart-notification--${type || 'info'} is-visible`;

      const action = this.el.querySelector('[data-cart-notification-action]');
      if (opts.undo && action) {
        action.textContent = opts.undoLabel || 'Undo';
        action.style.display = '';
        action.onclick = () => {
          if (typeof opts.onUndo === 'function') opts.onUndo();
          this.hide();
        };
      } else if (action) {
        action.style.display = 'none';
        action.onclick = null;
      }

      clearTimeout(this._tid);
      this._tid = setTimeout(() => this.hide(), opts.duration || 5000);
    },
    hide() {
      if (this.el) this.el.classList.remove('is-visible');
    }
  };

  window.cartToast = Toast;

  /* ── Main CartDrawer class ──────────────────────────────── */

  class CartDrawer {
    constructor() {
      this.drawer = $('[data-cart-drawer]');
      this.overlay = $('[data-cart-overlay]');
      this.body = this.drawer ? this.drawer.querySelector('[data-cart-body]') : null;
      this.footer = this.drawer ? this.drawer.querySelector('[data-cart-footer]') : null;
      this.eyebrow = $('[data-cart-eyebrow]');
      this.lastTrigger = null;
      this.removeTimers = new Map();
      this.noteDebounce = null;
      this.lastItemCount = null;
      this.upsellsHTML = null;
      this.translations = (CFG.translations) || {};
      this._skipRefreshOnce = false;
      // Títulos de variante con el color ya traducido (código ICG → nombre),
      // armados en Liquid por variant id. /cart.js solo trae el código crudo.
      this.variantLabels = Object.assign({}, CFG.variantLabels || {});
      this._labelFetches = new Set();

      if (this.drawer) {
        this._captureUpsells();
        this.init();
      }
    }

    init() {
      // open
      document.addEventListener('click', (e) => {
        const t = e.target.closest('[data-cart-toggle]');
        if (!t || !this.drawer) return;
        e.preventDefault();
        this.lastTrigger = t;
        this.open();
        this.refresh();
      });
      // close
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-cart-close], [data-cart-overlay]')) {
          e.preventDefault();
          this.close();
        }
      });
      // esc
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });
      // external open / refresh
      document.addEventListener('cart:open', () => { this.open(); this.refresh(); });
      document.addEventListener('cart:drawer:refresh', () => this.refresh());
      // cart:updated from other parts (product-card quick-add, popups, etc)
      // → sync count + refresh. When the update was already applied by this
      // drawer, the flag suppresses the redundant re-fetch/re-render.
      document.addEventListener('cart:updated', (e) => {
        if (e && e.detail) this.updateCount(e.detail.item_count);
        if (this._skipRefreshOnce) {
          this._skipRefreshOnce = false;
          return;
        }
        if (this.isOpen()) this.refresh();
      });

      // delegated qty/remove
      if (this.drawer) this.drawer.addEventListener('click', (e) => this._onClick(e));
      this.bindNotes();
    }

    /* ── open/close + a11y ───────────────────────────────── */

    isOpen() {
      return this.drawer && this.drawer.classList.contains('is-open');
    }

    open() {
      if (!this.drawer) return;
      this.drawer.classList.add('is-open');
      if (this.overlay) this.overlay.classList.add('is-open');
      this.lockBody();
      this.lastTrigger = document.activeElement;

      $$('body > *').forEach((n) => {
        if (
          n === this.drawer ||
          n === this.overlay ||
          n.contains(this.drawer) ||
          n.contains(this.overlay) ||
          n.tagName === 'SCRIPT' ||
          n.tagName === 'STYLE'
        ) return;
        n.setAttribute('aria-hidden', 'true');
        n.setAttribute('inert', '');
        n.dataset.drawerInert = '1';
      });
      requestAnimationFrame(() => {
        const f = this.drawer.querySelector('button, [href], [tabindex]:not([tabindex="-1"])');
        if (f) f.focus();
      });
      this._bindFocusTrap();
    }

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      if (this.overlay) this.overlay.classList.remove('is-open');
      this.unlockBody();
      $$('[data-drawer-inert="1"]').forEach((n) => {
        n.removeAttribute('aria-hidden');
        n.removeAttribute('inert');
        delete n.dataset.drawerInert;
      });
      if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
      }
      this._unbindFocusTrap();
    }

    lockBody() {
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
      document.body.classList.add('drawer-open');
    }

    unlockBody() {
      document.body.style.paddingRight = '';
      document.body.classList.remove('drawer-open');
    }

    _bindFocusTrap() {
      this._trapHandler = (e) => {
        if (e.key !== 'Tab' || !this.isOpen()) return;
        const focusables = this.drawer.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      };
      document.addEventListener('keydown', this._trapHandler);
    }

    _unbindFocusTrap() {
      if (this._trapHandler) document.removeEventListener('keydown', this._trapHandler);
      this._trapHandler = null;
    }

    /* ── data fetching ───────────────────────────────────── */

    async fetchCart() {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Cart fetch failed');
      return res.json();
    }

    async changeItem(key, qty) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const err = new Error('Cart change failed: ' + res.status);
        err.status = res.status;
        err.message2 = data.description || data.message || '';
        throw err;
      }
      return res.json();
    }

    /* ── refresh: choose in-place vs full re-render ──────── */

    async refresh() {
      let cart;
      try { cart = await this.fetchCart(); }
      catch (e) { console.error('[cart-drawer] refresh failed', e); return; }

      this.updateCount(cart.item_count);

      // Cross 0 ↔ >0: full re-render (this is the bug fix).
      // Otherwise, qty is the only typical change — patch in-place.
      const transitioned = this.lastItemCount !== null && this.lastItemCount !== cart.item_count;
      if (transitioned || this.lastItemCount === null) {
        this.renderAll(cart);
      } else {
        this.patch(cart);
      }
      this._syncUpsells(cart);
      this.lastItemCount = cart.item_count;
      // NOTE: do NOT dispatch cart:updated here. refresh() is triggered by the
      // cart:updated listener; re-dispatching from here creates an infinite
      // refresh loop that freezes the drawer. Individual mutations
      // (updateItem / removeWithUndo / addUpsell) dispatch cart:updated
      // themselves so external listeners (header count) stay in sync.
    }

    /* ── full re-render (uses server templates via JSON) ── */

    renderAll(cart) {
      if (!this.drawer) return;
      if (cart.item_count === 0) {
        this._renderEmpty();
        return;
      }
      this._renderItems(cart);
      this._resolveMissingLabels(cart);
    }

    _renderEmpty() {
      if (!this.body) return;
      const allProducts = CFG.allProductsUrl || '/collections/all';
      const emptyIcon = CFG.emptyIcon || '';
      const title = this._t('emptyTitle', 'Ready to shop?');
      const body = this._t('emptyBody', "Your cart is currently empty, but it doesn't have to stay that way. Start shopping now to find exactly what you're looking for.");
      this.body.innerHTML = `
        <div class="cart-drawer__empty" data-cart-empty>
          ${emptyIcon
            ? `<div class="cart-drawer__empty-icon"><img class="cart-drawer__empty-icon-img" src="${escapeHtml(emptyIcon)}" alt="" loading="lazy" width="40" height="40"></div>`
            : `<div class="cart-drawer__empty-icon" aria-hidden="true">
                 <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M19.84,7h-4.46c-.19-1.54-.6-3.21-1.5-4.16-1.04-1.11-2.73-1.11-3.77,0-.9.96-1.31,2.62-1.5,4.16h-4.46c-.61,0-1.08.54-.99,1.15l1.57,10.44c.29,1.96,1.98,3.41,3.96,3.41h6.61c1.98,0,3.66-1.45,3.96-3.41l1.57-10.44c.09-.6-.38-1.15-.99-1.15ZM10.84,3.52c.66-.7,1.66-.7,2.31,0,.68.73,1.04,2.1,1.22,3.48h-4.75c.18-1.37.53-2.75,1.22-3.48ZM18.27,18.45c-.22,1.48-1.47,2.55-2.97,2.55h-6.61c-1.5,0-2.74-1.07-2.97-2.55l-1.57-10.45h4.36c-.09,1.35-.03,2.43-.02,2.53.02.27.24.47.5.47.01,0,.02,0,.03,0,.28-.02.48-.25.47-.53-.03-.54-.05-1.46.03-2.47h4.95c.07,1.01.06,1.93.03,2.47-.02.28.19.51.47.53.29,0,.51-.19.53-.47,0-.1.07-1.18-.02-2.53h4.36s0,0,0,0l-1.57,10.45Z"/></svg>
               </div>`}
          <h3 class="cart-drawer__empty-title">${escapeHtml(title)}</h3>
          <p class="cart-drawer__empty-text">${escapeHtml(body)}</p>
          <div class="cart-drawer__empty-ctas">
            <a href="${escapeHtml(CFG.emptyLinkWomen || allProducts)}" class="cart-drawer__empty-cta">
              <span>${escapeHtml(this._t('shopWomen', 'Shop Women'))}</span>
            </a>
            <a href="${escapeHtml(CFG.emptyLinkMen || allProducts)}" class="cart-drawer__empty-cta">
              <span>${escapeHtml(this._t('shopMen', 'Shop Men'))}</span>
            </a>
          </div>
        </div>
      `;
      if (this.footer) {
        this.footer.remove();
        this.footer = null;
      }
    }

    _renderItems(cart) {
      if (!this.body) return;
      const threshold = Number(CFG.freeShippingThreshold) || 11000;
      const remaining = threshold - cart.total_price;
      const pct = Math.max(0, Math.min(100, (cart.total_price / threshold) * 100));
      const freeShipText = remaining > 0
        ? this._t('freeShippingHtml', 'You are __amount__ away from free shipping').replace('__amount__', money(remaining, cart.currency))
        : this._t('freeShipping', 'Your order qualifies for free shipping!');

      const itemsHtml = cart.items.map((item, i) => this._itemHTML(item, i + 1)).join('');
      const upsellsHtml = (CFG.showUpsells && this.upsellsHTML)
        ? this.upsellsHTML
        : (CFG.showUpsells ? this._upsellsSkeleton() : '');

      this.body.innerHTML = `
        <div class="cart-drawer__shipping" data-cart-shipping>
          <p class="cart-drawer__shipping-text" data-cart-shipping-text>${escapeHtml(freeShipText)}</p>
          <div class="cart-drawer__shipping-bar" aria-hidden="true">
            <div class="cart-drawer__shipping-progress" style="--progress: ${pct}%"></div>
          </div>
        </div>
        <ul class="cart-drawer__items" data-cart-items role="list">${itemsHtml}</ul>
        ${upsellsHtml}
        ${CFG.showNotes ? `
          <div class="cart-drawer__notes" data-cart-notes>
            <button type="button" class="cart-drawer__notes-toggle" data-notes-toggle aria-expanded="false">
              <span>${escapeHtml(this._t('addNote', 'Add a note to your order'))}</span>
              <svg class="cart-drawer__notes-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            <div class="cart-drawer__notes-content">
              <textarea name="note" class="cart-drawer__note-input" data-cart-note placeholder="${escapeHtml(this._t('notePlaceholder', 'Special instructions...'))}" rows="3"></textarea>
              <div class="cart-drawer__note-status" data-note-status aria-live="polite"></div>
            </div>
          </div>` : ''}
      `;

      // Footer
      const footerHtml = `
        <footer class="cart-drawer__footer" data-cart-footer>
          <div class="cart-drawer__subtotal">
            <span class="cart-drawer__subtotal-label">${escapeHtml(this._t('subtotal', 'Subtotal'))}</span>
            <span class="cart-drawer__subtotal-value" data-cart-total>${escapeHtml(moneyWithCurrency(cart.total_price, cart.currency))}</span>
          </div>
          <form action="${CFG.cartUrl || '/cart'}" method="post" class="cart-drawer__checkout-form" data-cart-checkout-form>
            <button type="submit" name="checkout" class="cart-drawer__checkout-btn">
              <span>${escapeHtml(CFG.checkoutButtonText || this._t('checkout', 'Checkout'))}</span>
              <span class="cart-drawer__checkout-sep" aria-hidden="true">—</span>
              <span data-cart-checkout-price>${escapeHtml(money(cart.total_price, cart.currency))}</span>
            </button>
          </form>
          <a href="${CFG.cartUrl || '/cart'}" class="cart-drawer__view-cart">${escapeHtml(this._t('viewCart', 'View cart'))}</a>
        </footer>
      `;
      if (this.footer) {
        this.footer.outerHTML = footerHtml;
      } else {
        this.drawer.insertAdjacentHTML('beforeend', footerHtml);
      }
      this.footer = this.drawer.querySelector('[data-cart-footer]');

      // Re-bind notes
      this.bindNotes();
      // Note input value
      const noteInput = this.drawer.querySelector('[data-cart-note]');
      if (noteInput && cart.note) noteInput.value = cart.note;
    }

    _variantLabel(item) {
      const known = this.variantLabels[String(item.variant_id || item.id || '')];
      return known || item.variant_title;
    }

    // Líneas agregadas después de cargar la página (p.ej. desde la PDP) no
    // están en variantLabels: se pide el HTML de esta misma sección (Section
    // Rendering API), que ya trae el título resuelto en Liquid, y se parcha
    // el texto en sitio. Una sola vez por variante para no entrar en bucle.
    _resolveMissingLabels(cart) {
      const missing = (cart.items || []).filter((item) => {
        const id = String(item.variant_id || item.id || '');
        return item.variant_title && item.variant_title !== 'Default Title'
          && !this.variantLabels[id] && !this._labelFetches.has(id);
      });
      if (!missing.length || !CFG.sectionId) return;
      missing.forEach((item) => this._labelFetches.add(String(item.variant_id || item.id || '')));

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(`${root}?sections=${encodeURIComponent(CFG.sectionId)}`, { credentials: 'same-origin' })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          const html = data && data[CFG.sectionId];
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('[data-cart-item][data-variant-id]').forEach((row) => {
            const labelEl = row.querySelector('[data-cart-variant-label]');
            if (labelEl) this.variantLabels[row.dataset.variantId] = labelEl.textContent.trim();
          });
          if (!this.drawer) return;
          this.drawer.querySelectorAll('[data-cart-item][data-variant-id]').forEach((row) => {
            const label = this.variantLabels[row.dataset.variantId];
            const labelEl = row.querySelector('[data-cart-variant-label]');
            if (label && labelEl) labelEl.textContent = label;
          });
        })
        .catch(() => {});
    }

    _itemHTML(item, line) {
      const titleEsc = escapeHtml(item.product_title);
      const variantTxt = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p class="cart-drawer__item-variant" data-cart-variant-label>${escapeHtml(this._variantLabel(item))}</p>` : '';
      const imageHTML = item.image
        ? `<img class="cart-drawer__item-img" src="${escapeHtml(item.image)}&width=160" alt="${titleEsc}" loading="lazy" width="80" height="80">`
        : `<div class="cart-drawer__item-img cart-drawer__item-img--placeholder" aria-hidden="true"></div>`;
      const price = money(item.final_line_price, item.currency || CFG.currency);
      return `
        <li class="cart-drawer__item" data-cart-item data-key="${escapeHtml(item.key)}" data-line="${line}" data-variant-id="${escapeHtml(String(item.variant_id || item.id || ''))}" data-qty="${item.quantity}">
          <a class="cart-drawer__item-image" href="${escapeHtml(item.url)}" aria-label="${titleEsc}">${imageHTML}</a>
          <div class="cart-drawer__item-info">
            <div class="cart-drawer__item-head">
              <a class="cart-drawer__item-title" href="${escapeHtml(item.url)}">${titleEsc}</a>
              <span class="cart-drawer__item-price" data-cart-line-price>${escapeHtml(price)}</span>
            </div>
            ${variantTxt}
            <div class="cart-drawer__item-foot">
              <div class="cart-drawer__qty" role="group" aria-label="${escapeHtml(this._t('qty', 'Qty'))}">
                <button type="button" class="cart-drawer__qty-btn" data-qty-decrease aria-label="${escapeHtml(this._t('decreaseQuantity', 'Decrease quantity'))}">
                  <svg width="10" height="2" viewBox="0 0 10 2" fill="currentColor" aria-hidden="true"><rect width="10" height="2"/></svg>
                </button>
                <span class="cart-drawer__qty-value" data-qty-input aria-live="polite">${item.quantity}</span>
                <button type="button" class="cart-drawer__qty-btn" data-qty-increase aria-label="${escapeHtml(this._t('increaseQuantity', 'Increase quantity'))}">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" aria-hidden="true"><rect y="4" width="10" height="2"/><rect x="4" width="2" height="10"/></svg>
                </button>
              </div>
              <button type="button" class="cart-drawer__remove" data-cart-remove>${escapeHtml(this._t('remove', 'Remove'))}</button>
            </div>
          </div>
        </li>
      `;
    }

    /* ── in-place patch (same item count, qty changed) ──── */

    patch(cart) {
      // Update shipping text + bar
      const threshold = Number(CFG.freeShippingThreshold) || 11000;
      const remaining = threshold - cart.total_price;
      const pct = Math.max(0, Math.min(100, (cart.total_price / threshold) * 100));
      const textEl = this.drawer.querySelector('[data-cart-shipping-text]');
      const barEl = this.drawer.querySelector('.cart-drawer__shipping-progress');
      if (textEl) {
        textEl.textContent = remaining > 0
          ? this._t('freeShippingHtml', 'You are __amount__ away from free shipping').replace('__amount__', money(remaining, cart.currency))
          : this._t('freeShipping', 'Your order qualifies for free shipping!');
      }
      if (barEl) barEl.style.setProperty('--progress', pct + '%');

      // Update per-line items
      const ul = this.drawer.querySelector('[data-cart-items]');
      if (ul) {
        const keys = new Set();
        cart.items.forEach((item) => {
          keys.add(String(item.key));
          const row = ul.querySelector(`[data-cart-item][data-key="${cssEscape(item.key)}"]`);
          if (row) {
            row.dataset.qty = String(item.quantity);
            const q = row.querySelector('[data-qty-input]');
            const p = row.querySelector('[data-cart-line-price]');
            if (q) q.textContent = item.quantity;
            if (p) p.textContent = money(item.final_line_price, item.currency);
          } else {
            // New item added without crossing 0 boundary (shouldn't happen, but safe)
            ul.insertAdjacentHTML('beforeend', this._itemHTML(item));
          }
        });
        // Remove rows that disappeared
        $$('[data-cart-item]', ul).forEach((row) => {
          if (!keys.has(row.dataset.key)) row.remove();
        });
      }

      // Update subtotal + checkout price
      const totalEl = this.drawer.querySelector('[data-cart-total]');
      if (totalEl) totalEl.textContent = moneyWithCurrency(cart.total_price, cart.currency);
      const checkoutEl = this.drawer.querySelector('[data-cart-checkout-price]');
      if (checkoutEl) checkoutEl.textContent = money(cart.total_price, cart.currency);
      this._resolveMissingLabels(cart);
    }

    /* ── count in header ─────────────────────────────────── */

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        if (el.classList.contains('header__cart-count')) {
          el.textContent = count;
          if (count > 0) el.removeAttribute('hidden');
          else el.setAttribute('hidden', '');
        } else {
          el.textContent = count > 0 ? `(${count})` : '';
        }
      });
      if (this.eyebrow) this.eyebrow.textContent = `(${count})`;
    }

    /* ── event delegation: qty / remove / notes ──────────── */

    _onClick(e) {
      const dec = e.target.closest('[data-qty-decrease]');
      const inc = e.target.closest('[data-qty-increase]');
      const rem = e.target.closest('[data-cart-remove]');
      const add = e.target.closest('[data-upsell-add]');
      if (dec) {
        e.preventDefault();
        const row = dec.closest('[data-cart-item]');
        if (row) {
          const next = Math.max(0, (parseInt(row.dataset.qty, 10) || 1) - 1);
          this.updateItem(row, next);
        }
      } else if (inc) {
        e.preventDefault();
        const row = inc.closest('[data-cart-item]');
        if (row) {
          const next = (parseInt(row.dataset.qty, 10) || 0) + 1;
          this.updateItem(row, next);
        }
      } else if (rem) {
        e.preventDefault();
        const row = rem.closest('[data-cart-item]');
        if (row) this.removeWithUndo(row);
      } else if (add) {
        e.preventDefault();
        const item = add.closest('[data-upsell]');
        if (item) this.addUpsell(item.dataset.variantId, add);
      }
    }

    bindNotes() {
      const toggle = this.drawer && this.drawer.querySelector('[data-notes-toggle]');
      const input = this.drawer && this.drawer.querySelector('[data-cart-note]');
      if (toggle && !toggle.dataset.bound) {
        toggle.dataset.bound = '1';
        toggle.addEventListener('click', () => {
          const notes = toggle.closest('[data-cart-notes]');
          if (!notes) return;
          const open = notes.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }
      if (input && !input.dataset.bound) {
        input.dataset.bound = '1';
        input.addEventListener('input', () => {
          clearTimeout(this.noteDebounce);
          this.noteDebounce = setTimeout(() => this.updateNote(input.value), 800);
        });
      }
    }

    /* ── updateItem (qty change) ─────────────────────────── */

    async updateItem(row, qty) {
      if (!row) return;
      const key = row.dataset.key;
      const prevQty = parseInt(row.dataset.qty, 10) || null;
      row.dataset.qty = String(qty);
      const q = row.querySelector('[data-qty-input]');
      if (q) q.textContent = qty;
      try {
        const cart = await this.changeItem(key, qty);
        if (qty === 0) {
          this.lastItemCount = null;
          this.refresh();
        } else {
          this.updateCount(cart.item_count);
          this.patch(cart);
        }
        this._emitUpdated(cart);
      } catch (e) {
        if (prevQty != null) {
          row.dataset.qty = String(prevQty);
          const q = row.querySelector('[data-qty-input]');
          if (q) q.textContent = prevQty;
        }
        const msg = e.status === 422 && e.message2
          ? e.message2
          : this._t('error', 'Error');
        Toast.show(msg, 'error', { duration: 3000 });
      }
    }

    /* ── removeWithUndo (instant: confirm server immediately,
       undo re-adds within the toast window) ──────────── */

    removeWithUndo(row) {
      if (!row) return;
      const key = row.dataset.key;
      const prevQty = parseInt(row.dataset.qty, 10) || 1;
      const variantId = row.dataset.variantId || null;

      if (this.removeTimers.has(key)) {
        clearTimeout(this.removeTimers.get(key));
        this.removeTimers.delete(key);
      }

      row.classList.add('is-removing');
      row.setAttribute('aria-hidden', 'true');

      this.changeItem(key, 0).then((cart) => {
        if (cart.item_count === 0) {
          this._renderEmpty();
          this.lastItemCount = 0;
        } else {
          this.updateCount(cart.item_count);
          this.patch(cart);
        }
        this._emitUpdated(cart);
      }).catch(() => {
        row.classList.remove('is-removing');
        row.removeAttribute('aria-hidden');
        Toast.show(this._t('error', 'Error'), 'error', { duration: 3000 });
      });

      const undo = () => {
        clearTimeout(timerId);
        this.removeTimers.delete(key);
        row.classList.remove('is-removing');
        row.removeAttribute('aria-hidden');
        Toast.hide();
        row.dataset.qty = String(prevQty);
        const q = row.querySelector('[data-qty-input]');
        if (q) q.textContent = prevQty;
        if (variantId) {
          fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: prevQty })
          })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((cart) => {
              this.updateCount(cart.item_count);
              this.lastItemCount = null;
              this.refresh();
            })
            .catch(() => {
              Toast.show(this._t('error', 'Error'), 'error', { duration: 3000 });
            });
        }
      };

      Toast.show(this._t('itemRemoved', 'Item removed'), 'info', {
        duration: 5000,
        undo: true,
        undoLabel: this._t('undo', 'Undo'),
        onUndo: undo
      });

      const timerId = setTimeout(() => {
        this.removeTimers.delete(key);
      }, 5000);

      this.removeTimers.set(key, timerId);
    }

    /* ── note update ─────────────────────────────────────── */

    async updateNote(value) {
      const status = this.drawer && this.drawer.querySelector('[data-note-status]');
      if (status) status.textContent = this._t('saving', 'Saving…');
      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ note: value })
        });
        if (res.ok) {
          if (status) {
            status.textContent = this._t('saved', 'Saved');
            setTimeout(() => { status.textContent = ''; }, 2000);
          }
        } else {
          if (status) status.textContent = this._t('errorSavingNote', 'Error saving note');
        }
      } catch (e) {
        if (status) status.textContent = this._t('errorSavingNote', 'Error saving note');
      }
    }

    /* ── upsells (server-rendered, preserved across re-renders) ── */

    _captureUpsells() {
      if (!CFG.showUpsells || !this.body) { this.upsellsHTML = null; return; }
      const block = this.body.querySelector('[data-cart-upsells]');
      this.upsellsHTML = block ? block.outerHTML : null;
    }

    // Oculta los upsells cuyo producto ya está en el carrito y deja visibles
    // solo los primeros `data-upsell-limit`. El HTML de upsells es del servidor
    // y se conserva entre re-renders, así que el carrito actual se aplica aquí.
    _syncUpsells(cart) {
      const grid = this.body && this.body.querySelector('[data-upsells-container]');
      if (!grid || !cart) return;
      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      const limit = Number(grid.dataset.upsellLimit) || 4;
      let visible = 0;
      grid.querySelectorAll('[data-upsell]').forEach((el) => {
        const show = !inCart.has(el.dataset.productId) && visible < limit;
        el.hidden = !show;
        if (show) visible += 1;
      });
      const block = grid.closest('[data-cart-upsells]');
      if (block) block.hidden = visible === 0;
    }

    _upsellsSkeleton() {
      return `
        <div class="cart-drawer__upsells" data-cart-upsells>
          <h3 class="cart-drawer__upsells-title">${escapeHtml(CFG.upsellsTitle || this._t('completeLook', 'Complete your look'))}</h3>
          <div class="cart-drawer__upsells-grid" data-upsells-container>
            ${Array.from({ length: 4 }).map(() => '<div class="cart-drawer__upsell-skeleton-item" aria-hidden="true"></div>').join('')}
          </div>
        </div>`;
    }

    /* ── quick-add from the upsell rail ─────────────────── */

    async addUpsell(variantId, btn) {
      if (!variantId || (btn && btn.disabled)) return;
      if (btn) {
        btn.disabled = true;
        btn.textContent = this._t('adding', 'Adding…');
      }
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });
        if (!res.ok) throw new Error('Add failed');
        const cart = await res.json();
        this.updateCount(cart.item_count);
        // Remove the added upsell so the rail reflects remaining options
        const item = btn && btn.closest('[data-upsell]');
        if (item) item.classList.add('is-added');
        this._emitUpdated(cart);
        if (this.isOpen()) this.refresh();
        else document.dispatchEvent(new CustomEvent('cart:open'));
      } catch (e) {
        console.error('[cart-drawer] upsell add failed', e);
        if (btn) {
          btn.disabled = false;
          btn.textContent = this._t('add', 'Add');
        }
      }
    }

    /* ── translation helper (reads the Liquid-injected store) */

    _t(key, fallback) {
      if (this.translations && this.translations[key] != null) {
        return this.translations[key];
      }
      return fallback || key;
    }

    /* ── emit cart:updated after a drawer-applied mutation ──
       Sets a one-shot flag so the global cart:updated listener does not
       re-refresh (this drawer already applied the change). External
       listeners (header count) still fire normally. */

    _emitUpdated(cart) {
      this._skipRefreshOnce = true;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    }
  }

  function cssEscape(s) {
    if (window.CSS && CSS.escape) return CSS.escape(String(s));
    return String(s).replace(/[^a-zA-Z0-9_-]/g, (c) => '\\' + c);
  }

  /* ── Init ──────────────────────────────────────────────── */

  function init() {
    if (!document.querySelector('[data-cart-drawer]')) return;
    Toast.init();
    window.cartDrawer = new CartDrawer();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
